import { useState, useEffect } from 'react';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Plus, Eye, Edit, Copy, Trash2, ChevronLeft, ChevronRight,
  ChevronUp, ChevronDown, ChevronsUpDown,
  HardDrive, Database, Server, Cpu
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import BulkEditBar from '../../components/BulkEditBar';
import ExportButton from '../../components/ExportButton';
import { toast } from 'sonner';
import SelectWithCreate from '../../components/ui/select-with-create';

const Storage = () => {
  const { canRead, canCreate, canUpdate, canDelete } = usePermissions('assets', 'storage');
  const [storages, setStorages] = useState([]);
  const [stats, setStats] = useState({ total: 0, total_tb: 0, por_tipo: [], por_marca: [] });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ search: '', empresa_id: '', site_id: '', brand_id: '', tipo_id: '', status_id: '', provider_id: '' });
  const [searchInput, setSearchInput] = useState(''); // Input inmediato para debounce
  const [sortConfig, setSortConfig] = useState({ field: 'asset_tag', dir: 'asc' });
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  const [pageSize, setPageSize] = useState(25);

  // Form options (for create/edit modal)
  const [options, setOptions] = useState({ empresas: [], sites: [], brands: [], tipos: [], estados: [], proveedores: [] });
  
  // Filter options (only values that exist in storage table)
  const [filterOptions, setFilterOptions] = useState({ empresas: [], sites: [], brands: [], tipos: [], estados: [], proveedores: [] });

  // Bulk selection
  const { 
    selectedIds, selectedCount, isAllSelected, isSomeSelected, 
    isSelected, toggleRow, toggleAll, clearSelection 
  } = useBulkSelection(storages);

  // Modals
  const [viewModal, setViewModal] = useState({ open: false, storage: null });
  const [editModal, setEditModal] = useState({ open: false, storage: null, isCopy: false });
  const [deleteModal, setDeleteModal] = useState({ open: false, storage: null });
  const [formData, setFormData] = useState({
    empresa_id: '', site_id: '', brand_id: '', tipo_id: '', status_id: '', provider_id: '',
    asset_tag: '', serial_number: '', modelo: '', capacidad_tb: '',
    num_discos: '', num_controladoras: '', firmware: '',
    ip_address: '', ip_mgmt: '', mac_address: '', ubicacion: '', descripcion: ''
  });
  const [saving, setSaving] = useState(false);

  const fetchStats = async () => {
    try {
      const response = await api.get('/assets/storage/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchOptions = async () => {
    try {
      const response = await api.get('/assets/storage/form/options');
      setOptions(response.data);
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/assets/storage/filter/options');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchStorages = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir
      });
      if (filters.search) params.append('search', filters.search);
      if (filters.empresa_id) params.append('empresa_id', filters.empresa_id);
      if (filters.site_id) params.append('site_id', filters.site_id);
      if (filters.brand_id) params.append('brand_id', filters.brand_id);
      if (filters.tipo_id) params.append('tipo_id', filters.tipo_id);
      if (filters.status_id) params.append('status_id', filters.status_id);
      if (filters.provider_id) params.append('provider_id', filters.provider_id);

      const response = await api.get(`/assets/storage?${params}`);
      setStorages(response.data.items);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Error al cargar storages');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchOptions();
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchStorages();
  }, [page, pageSize, filters.search, filters.empresa_id, filters.site_id, filters.brand_id, filters.tipo_id, filters.status_id, filters.provider_id, sortConfig]);

  // Debounce para filtro de búsqueda (800ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        setFilters(prev => ({ ...prev, search: searchInput }));
        setPage(1);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  // Stat card click handler
  const handleStatFilter = (filterType, tipoId = null) => {
    setActiveStatFilter(filterType);
    setPage(1);
    setSearchInput('');
    if (filterType === 'all') {
      setFilters({ search: '', empresa_id: '', site_id: '', brand_id: '', tipo_id: '', status_id: '', provider_id: '' });
    } else if (tipoId) {
      setFilters(prev => ({ ...prev, tipo_id: tipoId.toString(), search: '', empresa_id: '', site_id: '', brand_id: '', status_id: '', provider_id: '' }));
    }
  };

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  // Get icon for tipo
  const getTipoIcon = (tipoNombre) => {
    const iconMap = {
      'NAS': HardDrive,
      'SAN': Database,
      'DAS': Server,
      'All-Flash Array': Cpu,
    };
    return iconMap[tipoNombre] || HardDrive;
  };

  // View modal
  const handleView = (storage) => {
    setViewModal({ open: true, storage });
  };

  // Edit modal
  const handleEdit = (storage) => {
    setFormData({
      empresa_id: storage.empresa_id?.toString() || '',
      site_id: storage.site_id?.toString() || '',
      brand_id: storage.brand_id?.toString() || '',
      tipo_id: storage.tipo_id?.toString() || '',
      status_id: storage.status_id?.toString() || '',
      asset_tag: storage.asset_tag || '',
      serial_number: storage.serial_number || '',
      modelo: storage.modelo || '',
      capacidad_tb: storage.capacidad_tb?.toString() || '',
      num_discos: storage.num_discos?.toString() || '',
      num_controladoras: storage.num_controladoras?.toString() || '',
      firmware: storage.firmware || '',
      ip_address: storage.ip_address || '',
      ip_mgmt: storage.ip_mgmt || '',
      mac_address: storage.mac_address || '',
      ubicacion: storage.ubicacion || '',
      provider_id: storage.provider_id?.toString() || '',
      descripcion: storage.descripcion || ''
    });
    setEditModal({ open: true, storage, isCopy: false });
  };

  // Copy modal
  const handleCopy = (storage) => {
    setFormData({
      empresa_id: storage.empresa_id?.toString() || '',
      site_id: storage.site_id?.toString() || '',
      brand_id: storage.brand_id?.toString() || '',
      tipo_id: storage.tipo_id?.toString() || '',
      status_id: storage.status_id?.toString() || '',
      asset_tag: '', // Unique
      serial_number: '', // Unique
      modelo: storage.modelo || '',
      capacidad_tb: storage.capacidad_tb?.toString() || '',
      num_discos: storage.num_discos?.toString() || '',
      num_controladoras: storage.num_controladoras?.toString() || '',
      firmware: storage.firmware || '',
      ip_address: '', // Unique
      ip_mgmt: '', // Unique
      mac_address: '', // Unique
      ubicacion: storage.ubicacion || '',
      provider_id: storage.provider_id?.toString() || '',
      descripcion: storage.descripcion || ''
    });
    setEditModal({ open: true, storage: null, isCopy: true });
  };

  // Create new
  const handleCreate = () => {
    setFormData({
      empresa_id: '', site_id: '', brand_id: '', tipo_id: '', status_id: '', provider_id: '',
      asset_tag: '', serial_number: '', modelo: '', capacidad_tb: '',
      num_discos: '', num_controladoras: '', firmware: '',
      ip_address: '', ip_mgmt: '', mac_address: '', ubicacion: '', descripcion: ''
    });
    setEditModal({ open: true, storage: null, isCopy: false });
  };

  // Save
  const handleSave = async () => {
    if (!formData.asset_tag.trim()) {
      toast.error('El Asset Tag es requerido');
      return;
    }
    if (!formData.empresa_id) {
      toast.error('La Empresa es requerida');
      return;
    }
    if (!formData.site_id) {
      toast.error('El Site es requerido');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        empresa_id: parseInt(formData.empresa_id),
        site_id: parseInt(formData.site_id),
        brand_id: formData.brand_id ? parseInt(formData.brand_id) : null,
        tipo_id: formData.tipo_id ? parseInt(formData.tipo_id) : null,
        status_id: formData.status_id ? parseInt(formData.status_id) : null,
        asset_tag: formData.asset_tag.trim(),
        serial_number: formData.serial_number || null,
        modelo: formData.modelo || null,
        capacidad_tb: formData.capacidad_tb ? parseFloat(formData.capacidad_tb) : null,
        num_discos: formData.num_discos ? parseInt(formData.num_discos) : null,
        num_controladoras: formData.num_controladoras ? parseInt(formData.num_controladoras) : null,
        firmware: formData.firmware || null,
        ip_address: formData.ip_address || null,
        ip_mgmt: formData.ip_mgmt || null,
        mac_address: formData.mac_address || null,
        ubicacion: formData.ubicacion || null,
        provider_id: formData.provider_id ? parseInt(formData.provider_id) : null,
        descripcion: formData.descripcion || ''
      };

      if (editModal.storage && !editModal.isCopy) {
        await api.put(`/assets/storage/${editModal.storage.id}`, payload);
        toast.success('Storage actualizado exitosamente');
      } else {
        await api.post('/assets/storage', payload);
        toast.success('Storage creado exitosamente');
      }
      setEditModal({ open: false, storage: null, isCopy: false });
      fetchStorages();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    try {
      await api.delete(`/assets/storage/${deleteModal.storage.id}`);
      toast.success('Storage eliminado exitosamente');
      setDeleteModal({ open: false, storage: null });
      fetchStorages();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  // Quick create handlers
  const handleQuickCreateBrand = async (nombre) => {
    const response = await api.post('/assets/storage/quick-create/brand', { nombre });
    await fetchOptions();
    return response.data;
  };

  const handleQuickCreateTipo = async (nombre) => {
    const response = await api.post('/assets/storage/quick-create/tipo', { nombre });
    await fetchOptions();
    return response.data;
  };

  if (!canRead) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">No tienes permisos para ver Storage</p>
        </div>
      </Layout>
    );
  }

  // Get top 3 tipos for stats cards
  const topTipos = stats.por_tipo.filter(t => t.count > 0).slice(0, 3);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Storage</h1>
            <p className="page-subtitle">Gestion de NAS, SAN y Storages Enterprise</p>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton 
              endpoint="/assets/storage"
              filters={{ search: filters.search }}
              filename="storage"
            />
            {canCreate && (
              <Button onClick={handleCreate} className="btn btn-primary" data-testid="new-storage-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Storage
              </Button>
            )}
          </div>
        </div>

        {/* Stats - Clickeable para filtrar */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="storage-stats">
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-primary/50 ${activeStatFilter === 'all' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleStatFilter('all')}
            data-testid="stats-total"
          >
            <HardDrive className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value">{stats.total}</div>
              <div className="stats-card-label">Total</div>
            </div>
          </div>
          <div className="stats-card flex items-center gap-3" data-testid="stats-tb">
            <Database className="h-6 w-6 text-blue-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-blue-500">{stats.total_tb}</div>
              <div className="stats-card-label">TB Total</div>
            </div>
          </div>
          {topTipos.map((tipo, idx) => {
            const TipoIcon = getTipoIcon(tipo.nombre);
            const colors = ['text-green-500', 'text-amber-500', 'text-purple-500'];
            const rings = ['ring-green-500', 'ring-amber-500', 'ring-purple-500'];
            return (
              <div 
                key={tipo.id}
                className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:${rings[idx]}/50 ${activeStatFilter === `tipo_${tipo.id}` ? `ring-2 ${rings[idx]}` : ''}`}
                onClick={() => handleStatFilter(`tipo_${tipo.id}`, tipo.id)}
                data-testid={`stats-tipo-${tipo.id}`}
              >
                <TipoIcon className={`h-6 w-6 ${colors[idx]} flex-shrink-0`} strokeWidth={1.5} />
                <div>
                  <div className={`stats-card-value ${colors[idx]}`}>{tipo.count}</div>
                  <div className="stats-card-label">{tipo.nombre}</div>
                </div>
              </div>
            );
          })}
          {/* Fill empty slots */}
          {[...Array(3 - topTipos.length)].map((_, idx) => (
            <div key={`empty-${idx}`} className="stats-card flex items-center gap-3 opacity-50">
              <HardDrive className="h-6 w-6 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
              <div>
                <div className="stats-card-value text-muted-foreground">0</div>
                <div className="stats-card-label">-</div>
              </div>
            </div>
          ))}
        </div>

        {/* Table */}
        <Card className="card-subtle overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="table-subtle">
                    <thead>
                      <tr>
                        <th className="w-10">
                          <Checkbox
                            checked={isAllSelected}
                            indeterminate={isSomeSelected}
                            onCheckedChange={toggleAll}
                            aria-label="Seleccionar todos"
                          />
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('asset_tag')}
                        >
                          <div className="flex items-center gap-1">
                            ASSET TAG
                            <SortIcon field="asset_tag" />
                          </div>
                        </th>
                        <th>TIPO</th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('modelo')}
                        >
                          <div className="flex items-center gap-1">
                            MODELO
                            <SortIcon field="modelo" />
                          </div>
                        </th>
                        <th>MARCA</th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none text-right"
                          onClick={() => handleSort('capacidad_tb')}
                        >
                          <div className="flex items-center justify-end gap-1">
                            TB
                            <SortIcon field="capacidad_tb" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('ip_address')}
                        >
                          <div className="flex items-center gap-1">
                            IP
                            <SortIcon field="ip_address" />
                          </div>
                        </th>
                        <th>EMPRESA</th>
                        <th>PROVEEDOR</th>
                        <th>DESCRIPCIÓN</th>
                        <th>ESTADO</th>
                        <th className="w-32 text-center">ACCIONES</th>
                      </tr>
                      {/* Filters Row */}
                      <tr className="bg-muted/30">
                        <th className="w-10 py-2 px-4"></th>
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Buscar en todos los campos..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-search"
                          />
                        </th>
                        <th className="py-2 px-4">
                          <select
                            value={filters.tipo_id || ""}
                            onChange={(e) => handleFilterChange('tipo_id', e.target.value)}
                            className="h-7 w-full text-xs bg-background border border-border rounded px-2"
                          >
                            <option value="">Todos</option>
                            {filterOptions.tipos.map(t => (
                              <option key={t.id} value={t.id}>{t.nombre}</option>
                            ))}
                          </select>
                        </th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4">
                          <select
                            value={filters.brand_id || ""}
                            onChange={(e) => handleFilterChange('brand_id', e.target.value)}
                            className="h-7 w-full text-xs bg-background border border-border rounded px-2"
                          >
                            <option value="">Todas</option>
                            {filterOptions.brands.map(b => (
                              <option key={b.id} value={b.id}>{b.nombre}</option>
                            ))}
                          </select>
                        </th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4">
                          <select
                            value={filters.empresa_id || ""}
                            onChange={(e) => handleFilterChange('empresa_id', e.target.value)}
                            className="h-7 w-full text-xs bg-background border border-border rounded px-2"
                          >
                            <option value="">Todas</option>
                            {filterOptions.empresas.map(e => (
                              <option key={e.id} value={e.id}>{e.nombre}</option>
                            ))}
                          </select>
                        </th>
                        <th className="py-2 px-4">
                          <select
                            value={filters.provider_id || ""}
                            onChange={(e) => handleFilterChange('provider_id', e.target.value)}
                            className="h-7 w-full text-xs bg-background border border-border rounded px-2"
                          >
                            <option value="">Todos</option>
                            {filterOptions.proveedores?.map(p => (
                              <option key={p.id} value={p.id}>{p.nombre}</option>
                            ))}
                          </select>
                        </th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4">
                          <select
                            value={filters.status_id || ""}
                            onChange={(e) => handleFilterChange('status_id', e.target.value)}
                            className="h-7 w-full text-xs bg-background border border-border rounded px-2"
                          >
                            <option value="">Todos</option>
                            {filterOptions.estados?.map(e => (
                              <option key={e.id} value={e.id}>{e.nombre}</option>
                            ))}
                          </select>
                        </th>
                        <th className="w-32"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {storages.length === 0 ? (
                        <tr>
                          <td colSpan={12}>
                            <div className="empty-state">
                              <HardDrive className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                              <p className="empty-state-text">No se encontraron storages</p>
                            </div>
                          </td>
                        </tr>
                      ) : storages.map((storage) => (
                        <tr key={storage.id} data-testid={`storage-row-${storage.id}`} className={isSelected(storage.id) ? 'bg-primary/5' : ''}>
                          <td className="w-10">
                            <Checkbox
                              checked={isSelected(storage.id)}
                              onCheckedChange={() => toggleRow(storage.id)}
                              aria-label={`Seleccionar ${storage.asset_tag}`}
                            />
                          </td>
                          <td className="font-medium text-primary">{storage.asset_tag}</td>
                          <td>
                            {storage.tipo_nombre ? (
                              <span className="badge badge-outline">{storage.tipo_nombre}</span>
                            ) : '-'}
                          </td>
                          <td className="text-muted-foreground">{storage.modelo || '-'}</td>
                          <td className="text-muted-foreground">{storage.brand_nombre || '-'}</td>
                          <td className="text-right font-mono text-sm">
                            {storage.capacidad_tb ? `${storage.capacidad_tb} TB` : '-'}
                          </td>
                          <td className="font-mono text-sm">{storage.ip_address || '-'}</td>
                          <td className="text-muted-foreground">{storage.empresa_nombre || '-'}</td>
                          <td className="text-muted-foreground">{storage.proveedor_nombre || '-'}</td>
                          <td className="text-muted-foreground max-w-[220px] truncate" title={storage.descripcion || ''}>{storage.descripcion || '-'}</td>
                          <td>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                              storage.status_name?.toLowerCase().includes('activo') 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                : storage.status_name?.toLowerCase().includes('baja') || storage.status_name?.toLowerCase().includes('inactivo')
                                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                            }`}>
                              {storage.status_name || 'Sin estado'}
                            </span>
                          </td>
                          <td className="w-32">
                            <div className="flex justify-center gap-0.5">
                              <button
                                onClick={() => handleView(storage)}
                                className="action-btn"
                                title="Ver detalle"
                                data-testid={`view-storage-${storage.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canUpdate && handleEdit(storage)}
                                className={`action-btn action-btn-edit ${!canUpdate ? 'opacity-50' : ''}`}
                                disabled={!canUpdate}
                                title="Editar"
                                data-testid={`edit-storage-${storage.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canCreate && handleCopy(storage)}
                                className={`action-btn action-btn-copy ${!canCreate ? 'opacity-50' : ''}`}
                                disabled={!canCreate}
                                title="Copiar"
                                data-testid={`copy-storage-${storage.id}`}
                              >
                                <Copy className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canDelete && setDeleteModal({ open: true, storage })}
                                className={`action-btn action-btn-delete ${!canDelete ? 'opacity-50' : ''}`}
                                disabled={!canDelete}
                                title="Eliminar"
                                data-testid={`delete-storage-${storage.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-muted-foreground">
                      {total > 0 ? `${(page - 1) * pageSize + 1} - ${Math.min(page * pageSize, total)} de ${total}` : '0 registros'}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Mostrar:</span>
                      <Select
                        value={pageSize.toString()}
                        onValueChange={(value) => { setPageSize(parseInt(value)); setPage(1); }}
                      >
                        <SelectTrigger className="h-8 w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="25">25</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">{page} / {totalPages || 1}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages || totalPages === 0}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* View Modal */}
        <Dialog open={viewModal.open} onOpenChange={(open) => setViewModal({ open, storage: null })}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <HardDrive className="h-5 w-5 text-primary" />
                {viewModal.storage?.asset_tag}
              </DialogTitle>
            </DialogHeader>
            {viewModal.storage && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Asset Tag</Label>
                    <p className="font-medium">{viewModal.storage.asset_tag}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Serial Number</Label>
                    <p className="font-mono text-sm">{viewModal.storage.serial_number || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Tipo</Label>
                    <p>{viewModal.storage.tipo_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Marca</Label>
                    <p>{viewModal.storage.brand_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Modelo</Label>
                    <p>{viewModal.storage.modelo || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Capacidad</Label>
                    <p>{viewModal.storage.capacidad_tb ? `${viewModal.storage.capacidad_tb} TB` : '-'}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Discos</Label>
                    <p>{viewModal.storage.num_discos || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Controladoras</Label>
                    <p>{viewModal.storage.num_controladoras || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Firmware</Label>
                    <p className="font-mono text-sm">{viewModal.storage.firmware || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">IP Address</Label>
                    <p className="font-mono">{viewModal.storage.ip_address || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">IP Management</Label>
                    <p className="font-mono">{viewModal.storage.ip_mgmt || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">MAC Address</Label>
                    <p className="font-mono text-sm">{viewModal.storage.mac_address || '-'}</p>
                  </div>
                </div>
                <div className="col-span-2 grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Empresa</Label>
                    <p>{viewModal.storage.empresa_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Site</Label>
                    <p>{viewModal.storage.site_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Ubicacion</Label>
                    <p>{viewModal.storage.ubicacion || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Proveedor</Label>
                    <p>{viewModal.storage.proveedor_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Estado</Label>
                    <p>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        viewModal.storage.status_name?.toLowerCase().includes('activo') 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : viewModal.storage.status_name?.toLowerCase().includes('baja') || viewModal.storage.status_name?.toLowerCase().includes('inactivo')
                          ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                      }`}>
                        {viewModal.storage.status_name || 'Sin estado'}
                      </span>
                    </p>
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs text-muted-foreground">Descripción</Label>
                    <p className="whitespace-pre-wrap">{viewModal.storage.descripcion || '-'}</p>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit/Create/Copy Modal */}
        <Dialog open={editModal.open} onOpenChange={(open) => setEditModal({ open, storage: null, isCopy: false })}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <HardDrive className="h-5 w-5 text-primary" />
                {editModal.isCopy ? 'Copiar Storage' : editModal.storage ? 'Editar Storage' : 'Nuevo Storage'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="grid grid-cols-3 gap-4">
              {/* Row 1 - Identificacion */}
              <div className="space-y-1">
                <Label className="text-xs">Asset Tag *</Label>
                <Input
                  value={formData.asset_tag}
                  onChange={(e) => setFormData(prev => ({ ...prev, asset_tag: e.target.value }))}
                  placeholder="STG-001"
                  data-testid="input-asset-tag"
                  data-validation={editModal.isCopy ? (!formData.asset_tag ? 'required-empty' : '') : (!formData.asset_tag ? 'required-empty' : 'valid')}
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo unico</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Serial Number</Label>
                <Input
                  value={formData.serial_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, serial_number: e.target.value }))}
                  placeholder="SN123456"
                  data-validation={editModal.isCopy ? (!formData.serial_number ? 'required-empty' : '') : ''}
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo unico</p>}
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Tipo</Label>
                <SelectWithCreate
                  value={formData.tipo_id}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, tipo_id: v }))}
                  options={options.tipos}
                  placeholder="Seleccionar tipo"
                  onCreate={handleQuickCreateTipo}
                  createLabel="tipo"
                  data-validation={editModal.isCopy && formData.tipo_id ? 'valid' : ''}
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>

              {/* Row 2 - Marca y Modelo */}
              <div className="space-y-1">
                <Label className="text-xs">Marca</Label>
                <SelectWithCreate
                  value={formData.brand_id}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, brand_id: v }))}
                  options={options.brands}
                  placeholder="Seleccionar marca"
                  onCreate={handleQuickCreateBrand}
                  createLabel="marca"
                  data-validation={editModal.isCopy && formData.brand_id ? 'valid' : ''}
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Modelo</Label>
                <Input
                  value={formData.modelo}
                  onChange={(e) => setFormData(prev => ({ ...prev, modelo: e.target.value }))}
                  placeholder="ME5024, DS920+"
                  data-validation={editModal.isCopy && formData.modelo ? 'valid' : ''}
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Capacidad (TB)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.capacidad_tb}
                  onChange={(e) => setFormData(prev => ({ ...prev, capacidad_tb: e.target.value }))}
                  placeholder="100.00"
                  data-validation={editModal.isCopy && formData.capacidad_tb ? 'valid' : ''}
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>

              {/* Row 3 - Enterprise */}
              <div className="space-y-1">
                <Label className="text-xs">Num. Discos</Label>
                <Input
                  type="number"
                  value={formData.num_discos}
                  onChange={(e) => setFormData(prev => ({ ...prev, num_discos: e.target.value }))}
                  placeholder="24"
                  data-validation={editModal.isCopy && formData.num_discos ? 'valid' : ''}
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Controladoras</Label>
                <Input
                  type="number"
                  value={formData.num_controladoras}
                  onChange={(e) => setFormData(prev => ({ ...prev, num_controladoras: e.target.value }))}
                  placeholder="2"
                  data-validation={editModal.isCopy && formData.num_controladoras ? 'valid' : ''}
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Firmware</Label>
                <Input
                  value={formData.firmware}
                  onChange={(e) => setFormData(prev => ({ ...prev, firmware: e.target.value }))}
                  placeholder="v1.2.3"
                  data-validation={editModal.isCopy && formData.firmware ? 'valid' : ''}
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>

              {/* Row 4 - Network */}
              <div className="space-y-1">
                <Label className="text-xs">IP Address</Label>
                <Input
                  value={formData.ip_address}
                  onChange={(e) => setFormData(prev => ({ ...prev, ip_address: e.target.value }))}
                  placeholder="192.168.1.100"
                  data-validation={editModal.isCopy ? (!formData.ip_address ? 'required-empty' : '') : ''}
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo unico</p>}
              </div>

              <div className="space-y-1">
                <Label className="text-xs">IP Management</Label>
                <Input
                  value={formData.ip_mgmt}
                  onChange={(e) => setFormData(prev => ({ ...prev, ip_mgmt: e.target.value }))}
                  placeholder="192.168.1.101"
                  data-validation={editModal.isCopy ? (!formData.ip_mgmt ? 'required-empty' : '') : ''}
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo unico</p>}
              </div>

              <div className="space-y-1">
                <Label className="text-xs">MAC Address</Label>
                <Input
                  value={formData.mac_address}
                  onChange={(e) => setFormData(prev => ({ ...prev, mac_address: e.target.value }))}
                  placeholder="AA:BB:CC:DD:EE:FF"
                  data-validation={editModal.isCopy ? (!formData.mac_address ? 'required-empty' : '') : ''}
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo unico</p>}
              </div>

              {/* Row 5 - Organizacion */}
              <div className="space-y-1">
                <Label className="text-xs">Empresa *</Label>
                <select
                  value={formData.empresa_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, empresa_id: e.target.value }))}
                  className="h-9 w-full text-sm bg-background border border-border rounded px-2"
                  data-testid="select-empresa"
                  data-validation={editModal.isCopy ? (formData.empresa_id ? 'valid' : '') : (!formData.empresa_id ? 'required-empty' : 'valid')}
                >
                  <option value="">Seleccionar...</option>
                  {options.empresas.map(e => (
                    <option key={e.id} value={e.id}>{e.nombre}</option>
                  ))}
                </select>
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Site *</Label>
                <select
                  value={formData.site_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, site_id: e.target.value }))}
                  className="h-9 w-full text-sm bg-background border border-border rounded px-2"
                  data-testid="select-site"
                  data-validation={editModal.isCopy ? (formData.site_id ? 'valid' : '') : (!formData.site_id ? 'required-empty' : 'valid')}
                >
                  <option value="">Seleccionar...</option>
                  {options.sites.map(s => (
                    <option key={s.id} value={s.id}>{s.nombre}</option>
                  ))}
                </select>
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Ubicacion</Label>
                <Input
                  value={formData.ubicacion}
                  onChange={(e) => setFormData(prev => ({ ...prev, ubicacion: e.target.value }))}
                  placeholder="Rack 1, U20"
                  data-validation={editModal.isCopy && formData.ubicacion ? 'valid' : ''}
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Estado</Label>
                <select
                  value={formData.status_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, status_id: e.target.value }))}
                  className="h-9 w-full text-sm bg-background border border-border rounded px-2"
                  data-testid="select-status"
                  data-validation={editModal.isCopy && formData.status_id ? 'valid' : ''}
                >
                  <option value="">Sin estado</option>
                  {options.estados?.map(e => (
                    <option key={e.id} value={e.id}>{e.nombre}</option>
                  ))}
                </select>
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Proveedor</Label>
                <select
                  value={formData.provider_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, provider_id: e.target.value }))}
                  className="h-9 w-full text-sm bg-background border border-border rounded px-2"
                  data-testid="select-provider"
                >
                  <option value="">Sin proveedor</option>
                  {options.proveedores?.map(p => (
                    <option key={p.id} value={p.id}>{p.nombre}</option>
                  ))}
                </select>
                {editModal.isCopy && formData.provider_id && <p className="text-xs text-green-600">Copiado</p>}
              </div>

              <div className="space-y-1 col-span-3">
                <Label className="text-xs">Descripción</Label>
                <Textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                  placeholder="Descripción / notas del storage..."
                  rows={3}
                  data-testid="input-descripcion"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModal({ open: false, storage: null, isCopy: false })}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="btn btn-primary">
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, storage: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Eliminar Storage
              </DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground">
              Estas seguro que deseas eliminar el storage <strong>{deleteModal.storage?.asset_tag}</strong>?
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModal({ open: false, storage: null })}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Edit Bar */}
        <BulkEditBar
          selectedIds={selectedIds}
          onClear={clearSelection}
          onSuccess={() => { fetchStorages(); fetchStats(); }}
          endpoint="/assets/storage"
          options={{
            sites: options.sites,
            empresas: options.empresas,
            statuses: options.estados?.map(e => ({ id: e.id, nombre: e.nombre || e.name }))
          }}
          fields={['site_id', 'empresa_id', 'status_id']}
        />
      </div>
    </Layout>
  );
};

export default Storage;
